import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Modal } from "./Modal";
import { BatchTagPicker } from "./BatchTagPicker";
import { api } from "../api/client";
import type { QuerySnapshot } from "./SelectionContext";
import type { TagSummary } from "@media-viewer/shared";

interface TagModalProps {
  open: boolean;
  onClose: () => void;
  /** id-based action target. Used when `query` is not provided. */
  mediaIds: number[];
  /**
   * When present, the modal dispatches the by-query variant — server-side
   * resolves the filter and acts on the full matched set. `mediaIds` is
   * ignored in that case (caller passes `[]`).
   */
  query?: QuerySnapshot;
  /** Display-only matched count, used in the modal header when in query mode. */
  matched?: number;
}

export function TagModal({ open, onClose, mediaIds, query, matched }: TagModalProps) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<TagSummary[]>([]);

  const close = () => {
    setSelected([]);
    onClose();
  };

  const apply = useMutation({
    mutationFn: async () => {
      const toAdd = selected.map((t) => t.id);
      if (toAdd.length === 0) return { ok: true };
      if (query) {
        return api.bulkTagByQuery({
          kind: query.kind,
          tag: query.tag,
          structured: query.structured,
          addTagIds: toAdd,
          removeTagIds: [],
        });
      }
      return api.bulkTag(mediaIds, toAdd, []);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media"] });
      qc.invalidateQueries({ queryKey: ["tags"] });
      qc.invalidateQueries({ queryKey: ["neighbors"] });
      close();
    },
  });

  const headerCount = query ? (matched ?? 0) : mediaIds.length;
  const headerLabel = query
    ? `${headerCount.toLocaleString()} matching item${headerCount === 1 ? "" : "s"}`
    : `${headerCount} item${headerCount === 1 ? "" : "s"}`;

  return (
    <Modal
      open={open}
      onClose={close}
      title={`Tag ${headerLabel}`}
      footer={
        <>
          <button onClick={close} className="rounded bg-zinc-700 px-3 py-1.5 hover:bg-zinc-600">
            Cancel
          </button>
          <button
            onClick={() => apply.mutate()}
            disabled={apply.isPending || selected.length === 0}
            className="rounded bg-emerald-700 px-3 py-1.5 text-emerald-100 hover:bg-emerald-600 disabled:opacity-50"
          >
            Apply
          </button>
        </>
      }
    >
      {open && (
        <BatchTagPicker selected={selected} onChange={setSelected} heading={null} />
      )}
    </Modal>
  );
}
